import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, NavLink } from "react-router-dom";
import { Spinner, Form } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../Config";
import TeacherMenu from "./TeacherMenu";
import SessionsDropDown from "../Shared/SessionsDropDown";
import SubjectDropDown from "../Shared/SubjectDropDown";

function TeacherDiaryManager() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(parseInt(localStorage.getItem("ID"), 10) || 0);
  const [MemberType] = useState(localStorage.getItem("MemberType") || "Teacher");
  const [SessionID, setSessionID] = useState(localStorage.getItem("DefaultSession") || 0);
  const [assignedSections, setAssignedSections] = useState([]);
  const [loadingAssignedSections, setLoadingAssignedSections] = useState(false);
  const [selectedAssignedSection, setSelectedAssignedSection] = useState(0);
  const [ClassID, setClassID] = useState(0);
  const [SectionID, setSectionID] = useState(0);
  const [SubjectID, setSubjectID] = useState(0);
  const [DiaryTypeID, setDiaryTypeID] = useState(0);
  const [DiaryTitle, setDiaryTitle] = useState("");
  const [DiaryText, setDiaryText] = useState("");
  const [editingDiaryID, setEditingDiaryID] = useState(0);
  const [DueDate, setDueDate] = useState("");
  const [IsSubmissionRequired, setIsSubmissionRequired] = useState(false);
  const [AllowComments, setAllowComments] = useState(false);
  const [NotifyByApp, setNotifyByApp] = useState(true);
  const [VisibleTo, setVisibleTo] = useState("Student");

  const [diaryTypes, setDiaryTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Draft");
  const [myDiaries, setMyDiaries] = useState([]);
  const [reviewDiaries, setReviewDiaries] = useState([]);
  const [diaryAttachments, setDiaryAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [selectedDiaryForReview, setSelectedDiaryForReview] = useState(0);
  const [readStatus, setReadStatus] = useState([]);
  const [ackStatus, setAckStatus] = useState([]);
  const [submissionReview, setSubmissionReview] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);

  const commonPayload = useMemo(
    () => ({
      AccessKey,
      UserID,
      MemberType,
    }),
    [AccessKey, MemberType, UserID]
  );

  const loadDiaryTypes = () => {
    setLoadingTypes(true);
    const payloads = [
      commonPayload,
      { ...commonPayload, MemberType: "Teacher" },
      { ...commonPayload, MemberType: "Admin" },
    ];

    const tryLoad = (index) => {
      if (index >= payloads.length) {
        setDiaryTypes([]);
        toast.error("Could not load diary types.");
        setLoadingTypes(false);
        return;
      }

      axios({
        method: "post",
        url: config.base_url + "Diary/GetDiaryTypes",
        headers: { "Content-Type": "application/json" },
        data: payloads[index],
      })
        .then((res) => {
          if (res.data?.status_code === 1) {
            setDiaryTypes(res.data.ListofRecords || []);
            setLoadingTypes(false);
          } else {
            tryLoad(index + 1);
          }
        })
        .catch(() => tryLoad(index + 1));
    };

    tryLoad(0);
  };

  const loadAssignedSections = () => {
    setLoadingAssignedSections(true);
    axios({
      method: "post",
      url: config.base_url + "Sections/GetSectionsByTeacherID",
      headers: { "Content-Type": "application/json" },
      data: {
        ...commonPayload,
        PageNo: 1,
        PageSize: 1000,
        SessionID: parseInt(SessionID, 10) || 0,
        TeacherID: UserID,
      },
    })
      .then((res) => {
        if (res.data?.status_code !== 0) {
          setAssignedSections(res.data.ListofRecords || []);
        } else {
          setAssignedSections([]);
        }
      })
      .catch(() => setAssignedSections([]))
      .finally(() => setLoadingAssignedSections(false));
  };

  const loadMyDiaries = (status) => {
    setLoadingList(true);
    axios({
      method: "post",
      url: config.base_url + "Diary/GetTeacherDiaries",
      headers: { "Content-Type": "application/json" },
      data: { ...commonPayload, TeacherID: UserID, Status: status },
    })
      .then((res) => {
        if (res.data?.status_code === 1) {
          setMyDiaries(res.data.ListofRecords || []);
        } else {
          setMyDiaries([]);
        }
      })
      .catch(() => setMyDiaries([]))
      .finally(() => setLoadingList(false));
  };

  const loadReviewDiaries = () => {
    axios({
      method: "post",
      url: config.base_url + "Diary/GetTeacherDiaries",
      headers: { "Content-Type": "application/json" },
      data: { ...commonPayload, TeacherID: UserID, Status: "" },
    })
      .then((res) => {
        if (res.data?.status_code === 1) {
          setReviewDiaries(res.data.ListofRecords || []);
        } else {
          setReviewDiaries([]);
        }
      })
      .catch(() => setReviewDiaries([]));
  };

  const loadAttachments = (diaryId) => {
    if (!(diaryId > 0)) {
      setDiaryAttachments([]);
      return;
    }
    setLoadingAttachments(true);
    axios({
      method: "post",
      url: config.base_url + "Diary/GetDiaryAttachments",
      headers: { "Content-Type": "application/json" },
      data: { ...commonPayload, DiaryID: diaryId },
    })
      .then((res) => {
        if (res.data?.status_code === 1) {
          setDiaryAttachments(res.data.ListofRecords || []);
        } else {
          setDiaryAttachments([]);
        }
      })
      .catch(() => setDiaryAttachments([]))
      .finally(() => setLoadingAttachments(false));
  };

  const addAttachmentRow = async (diaryId, fileMeta) => {
    if (!(diaryId > 0) || !fileMeta?.AttachmentPath) return;
    const res = await axios({
      method: "post",
      url: config.base_url + "Diary/AddDiaryAttachment",
      headers: { "Content-Type": "application/json" },
      data: {
        ...commonPayload,
        DiaryID: diaryId,
        FileName: fileMeta.FileName,
        AttachmentPath: fileMeta.AttachmentPath,
        FileType: fileMeta.FileType,
        FileSizeKB: fileMeta.FileSizeKB,
      },
    });
    if (res.data?.status_code !== 1) {
      throw new Error(res.data?.status_message || "Could not save diary attachment.");
    }
  };

  const uploadAttachment = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setUploadingAttachment(true);
      try {
        const uploadRes = await axios({
          method: "post",
          url: config.base_url + "Diary/UploadDiaryAttachment",
          headers: { "Content-Type": "application/json" },
          data: {
            ...commonPayload,
            FileName: file.name,
            FileContent: reader.result,
            FileType: file.type || "",
            FileSizeKB: Math.ceil((file.size || 0) / 1024),
          },
        });

        if (uploadRes.data?.status_code !== 1) {
          toast.error(uploadRes.data?.status_message || "Could not upload attachment.");
          return;
        }

        const fileMeta = {
          FileName: uploadRes.data.FileName || file.name,
          AttachmentPath: uploadRes.data.AttachmentPath,
          FileType: uploadRes.data.FileType || file.type || "",
          FileSizeKB:
            uploadRes.data.FileSizeKB ?? Math.ceil((file.size || 0) / 1024),
        };

        if (editingDiaryID > 0) {
          await addAttachmentRow(editingDiaryID, fileMeta);
          loadAttachments(editingDiaryID);
        } else {
          setPendingAttachments((prev) => [...prev, fileMeta]);
        }
        toast.success("Attachment added.");
      } catch {
        toast.error("Could not upload attachment.");
      } finally {
        setUploadingAttachment(false);
      }
    };
    reader.onerror = () => toast.error("Could not read selected file.");
    reader.readAsDataURL(file);
  };

  const deleteAttachment = async (id) => {
    try {
      const res = await axios({
        method: "post",
        url: config.base_url + "Diary/DeleteDiaryAttachment",
        headers: { "Content-Type": "application/json" },
        data: { ...commonPayload, ID: id },
      });
      if (res.data?.status_code === 1) {
        setDiaryAttachments((prev) => prev.filter((a) => a.ID !== id));
        toast.success("Attachment removed.");
      } else {
        toast.error(res.data?.status_message || "Could not remove attachment.");
      }
    } catch {
      toast.error("Could not remove attachment.");
    }
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    document.title = "Teacher Diary";
    loadDiaryTypes();
  }, []);

  useEffect(() => {
    loadAssignedSections();
  }, [SessionID]);

  useEffect(() => {
    if (!(selectedAssignedSection > 0)) {
      setClassID(0);
      setSectionID(0);
      return;
    }
    const selected = assignedSections.find(
      (s) => (s.Section_ID || 0) === selectedAssignedSection
    );
    if (!selected) {
      setClassID(0);
      setSectionID(0);
      return;
    }
    setSectionID(selected.Section_ID || 0);
    setClassID(
      selected.SelectedClass?.Class_Id ||
      selected.SelectedClass?.ID ||
      selected.Class_Id ||
      selected.Class_ID ||
      0
    );
  }, [selectedAssignedSection, assignedSections]);

  useEffect(() => {
    if (assignedSections.length > 0 && !(selectedAssignedSection > 0)) {
      setSelectedAssignedSection(assignedSections[0].Section_ID || 0);
    }
  }, [assignedSections, selectedAssignedSection]);

  useEffect(() => {
    loadMyDiaries(statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    loadReviewDiaries();
  }, []);

  const fetchStudentsForAssign = async () => {
    const res = await axios({
      method: "post",
      url: config.base_url + "Student/AdvanceSearch",
      headers: { "Content-Type": "application/json" },
      data: {
        ...commonPayload,
        pageNo: 1,
        pageSize: 10000,
        SessionID: parseInt(SessionID, 10) || 0,
        Class_Id: parseInt(ClassID, 10) || 0,
        Section_Id: parseInt(SectionID, 10) || 0,
        OrderBy: "Student_Name",
      },
    });
    const list = res.data?.ListofRecords || [];
    return list.map((s) => s.Student_ID).filter((id) => id > 0);
  };

  const handleSave = async (publishNow = false) => {
    if (!SessionID || !DiaryTypeID || !DiaryTitle.trim() || !DiaryText.trim()) {
      toast.error("Session, diary type, title and diary text are required.");
      return;
    }

    if (!(assignedSections.length > 0)) {
      toast.error("No section is assigned to this teacher for the selected session.");
      return;
    }

    if (!ClassID || !SectionID) {
      toast.error("Please select an assigned section.");
      return;
    }

    setSaving(true);
    try {
      const saveRes = await axios({
        method: "post",
        url: config.base_url + "Diary/SaveDiary",
        headers: { "Content-Type": "application/json" },
        data: {
          ...commonPayload,
          DiaryID: editingDiaryID,
          TeacherID: UserID,
          Dated: new Date(),
          SessionID: parseInt(SessionID, 10) || 0,
          Class_Id: parseInt(ClassID, 10) || 0,
          Section_Id: parseInt(SectionID, 10) || 0,
          Subject_ID: parseInt(SubjectID, 10) || 0,
          DiaryTypeID: parseInt(DiaryTypeID, 10) || 0,
          DiaryTitle: DiaryTitle.trim(),
          DiaryText: DiaryText.trim(),
          TargetType: "Section",
          DueDate: DueDate ? new Date(DueDate) : null,
          IsSubmissionRequired,
          IsPublished: publishNow,
          VisibleTo,
          AllowComments,
          NotifyByApp,
          NotifyBySMS: false,
          NotifyByEmail: false,
          NotifyByWhatsApp: false,
          Is_Active: true,
        },
      });

      if (saveRes.data?.status_code !== 1 || !(saveRes.data.DiaryID > 0)) {
        toast.error(saveRes.data?.status_message || "Could not save diary.");
        return;
      }

      const diaryId = saveRes.data.DiaryID;
      if (pendingAttachments.length > 0) {
        for (const fileMeta of pendingAttachments) {
          await addAttachmentRow(diaryId, fileMeta);
        }
      }
      const studentIds = await fetchStudentsForAssign();
      if (studentIds.length > 0) {
        await axios({
          method: "post",
          url: config.base_url + "Diary/AssignDiaryToStudents",
          headers: { "Content-Type": "application/json" },
          data: {
            ...commonPayload,
            DiaryID: diaryId,
            SelectedStudents: studentIds,
          },
        });
      }

      if (publishNow) {
        await axios({
          method: "post",
          url: config.base_url + "Diary/PublishDiary",
          headers: { "Content-Type": "application/json" },
          data: {
            ...commonPayload,
            DiaryID: diaryId,
          },
        });
      }

      toast.success(publishNow ? "Diary published successfully." : "Diary saved as draft.");
      setDiaryTitle("");
      setDiaryText("");
      setEditingDiaryID(0);
      setDueDate("");
      setSubjectID(0);
      setDiaryTypeID(0);
      setSelectedAssignedSection(0);
      setClassID(0);
      setSectionID(0);
      setIsSubmissionRequired(false);
      setAllowComments(false);
      setNotifyByApp(true);
      setDiaryAttachments([]);
      setPendingAttachments([]);
      loadMyDiaries(statusFilter);
      loadReviewDiaries();
    } catch {
      toast.error("Could not save diary.");
    } finally {
      setSaving(false);
    }
  };

  const startEditDraft = (d) => {
    setEditingDiaryID(d.ID || 0);
    setSessionID(d.SessionID || localStorage.getItem("DefaultSession") || 0);
    setClassID(d.ClassID || 0);
    setSectionID(d.SectionID || 0);
    setSubjectID(d.SubjectID || 0);
    setDiaryTypeID(d.DiaryTypeID || 0);
    setDiaryTitle(d.DiaryTitle || "");
    setDiaryText(d.DiaryText || "");
    setDueDate(d.DueDate ? String(d.DueDate).substring(0, 10) : "");
    setIsSubmissionRequired(!!d.IsSubmissionRequired);
    setAllowComments(!!d.AllowComments);
    setNotifyByApp(d.NotifyByApp !== false);
    setVisibleTo(d.VisibleTo || "Student");

    const assigned = assignedSections.find((s) => (s.Section_ID || 0) === (d.SectionID || 0));
    setSelectedAssignedSection(assigned ? assigned.Section_ID : 0);
    setPendingAttachments([]);
    loadAttachments(d.ID || 0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingDiaryID(0);
    setDiaryTitle("");
    setDiaryText("");
    setDueDate("");
    setSubjectID(0);
    setDiaryTypeID(0);
    setIsSubmissionRequired(false);
    setAllowComments(false);
    setNotifyByApp(true);
    setVisibleTo("Student");
    setDiaryAttachments([]);
    setPendingAttachments([]);
  };

  const publishDraft = async (id) => {
    try {
      const res = await axios({
        method: "post",
        url: config.base_url + "Diary/PublishDiary",
        headers: { "Content-Type": "application/json" },
        data: { ...commonPayload, DiaryID: id },
      });
      if (res.data?.status_code === 1) {
        toast.success("Diary published.");
        loadMyDiaries(statusFilter);
        loadReviewDiaries();
      } else {
        toast.error(res.data?.status_message || "Could not publish diary.");
      }
    } catch {
      toast.error("Could not publish diary.");
    }
  };

  const loadReviewPanels = async (diaryId) => {
    if (!(diaryId > 0)) {
      setReadStatus([]);
      setAckStatus([]);
      setSubmissionReview([]);
      return;
    }

    setReviewLoading(true);
    try {
      const [readRes, ackRes, subRes] = await Promise.all([
        axios({
          method: "post",
          url: config.base_url + "Diary/GetDiaryReadStatus",
          headers: { "Content-Type": "application/json" },
          data: { ...commonPayload, DiaryID: diaryId },
        }),
        axios({
          method: "post",
          url: config.base_url + "Diary/GetDiaryAcknowledgementStatus",
          headers: { "Content-Type": "application/json" },
          data: { ...commonPayload, DiaryID: diaryId },
        }),
        axios({
          method: "post",
          url: config.base_url + "Diary/GetDiarySubmissionReview",
          headers: { "Content-Type": "application/json" },
          data: { ...commonPayload, DiaryID: diaryId },
        }),
      ]);

      setReadStatus(readRes.data?.ListofRecords || []);
      setAckStatus(ackRes.data?.ListofRecords || []);
      setSubmissionReview(subRes.data?.ListofRecords || []);
    } catch {
      setReadStatus([]);
      setAckStatus([]);
      setSubmissionReview([]);
      toast.error("Could not load diary review panels.");
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <TeacherMenu />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><NavLink to="/teacher/dashboard">Home</NavLink></li>
                    <li className="breadcrumb-item active">Teacher Diary</li>
                  </ol>
                </nav>
                <div className="page-title">Teacher Diary</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-3">Create Diary</h6>
              {editingDiaryID > 0 ? (
                <div className="alert alert-info py-2">
                  Editing draft #{editingDiaryID}
                </div>
              ) : null}
              <Form>
                <div className="row gy-2">
                  <div className="col-md-3">
                    <label>Select Session</label>
                    <select className="form-select" value={SessionID} onChange={(e) => setSessionID(e.target.value)}>
                      <option value="0">---All---</option>
                      <SessionsDropDown selectedSession={SessionID} />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Select Assigned Section</label>
                    <select
                      className="form-select"
                      value={selectedAssignedSection}
                      disabled={loadingAssignedSections || assignedSections.length === 0}
                      onChange={(e) => setSelectedAssignedSection(parseInt(e.target.value, 10) || 0)}
                    >
                      <option value="0">
                        {loadingAssignedSections ? "Loading..." : "Please Select"}
                      </option>
                      {assignedSections.map((s) => (
                        <option key={s.Section_ID} value={s.Section_ID}>
                          {s.SelectedClass?.Class_Name || s.Class_Name || `Class ${s.Class_ID || ""}`} - {s.Section_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Class</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        assignedSections.find((s) => (s.Section_ID || 0) === selectedAssignedSection)?.SelectedClass?.Class_Name ||
                        assignedSections.find((s) => (s.Section_ID || 0) === selectedAssignedSection)?.Class_Name ||
                        ""
                      }
                      readOnly
                    />
                  </div>
                  <div className="col-md-3">
                    <label>Section</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        assignedSections.find((s) => (s.Section_ID || 0) === selectedAssignedSection)?.Section_Name ||
                        ""
                      }
                      readOnly
                    />
                  </div>

                  <div className="col-md-3">
                    <label>Select Subject (optional)</label>
                    <select className="form-select" value={SubjectID} onChange={(e) => setSubjectID(e.target.value)}>
                      <option value="0">General</option>
                      <SubjectDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Diary Type</label>
                    <select className="form-select" value={DiaryTypeID} onChange={(e) => setDiaryTypeID(e.target.value)}>
                      <option value="0">{loadingTypes ? "Loading..." : "Please Select"}</option>
                      {diaryTypes.map((t) => (
                        <option key={t.ID} value={t.ID}>{t.DiaryTypeName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Visible To</label>
                    <select className="form-select" value={VisibleTo} onChange={(e) => setVisibleTo(e.target.value)}>
                      <option value="Student">Students</option>
                      <option value="Parent">Parents</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Due Date (optional)</label>
                    <input type="date" className="form-control" value={DueDate} onChange={(e) => setDueDate(e.target.value)} />
                  </div>

                  <div className="col-md-12">
                    <label>Diary Title</label>
                    <input type="text" className="form-control" value={DiaryTitle} onChange={(e) => setDiaryTitle(e.target.value)} />
                  </div>
                  <div className="col-md-12">
                    <label>Diary Note</label>
                    <textarea className="form-control" rows={4} value={DiaryText} onChange={(e) => setDiaryText(e.target.value)} />
                  </div>

                  <div className="col-md-12">
                    <label>Attachments</label>
                    <input
                      type="file"
                      className="form-control"
                      onChange={(e) => uploadAttachment(e.target.files?.[0])}
                      disabled={uploadingAttachment}
                    />
                    {uploadingAttachment ? (
                      <div className="small text-muted mt-1">Uploading attachment...</div>
                    ) : null}
                  </div>

                  {pendingAttachments.length > 0 ? (
                    <div className="col-md-12">
                      <div className="small fw-semibold mb-1">Pending Attachments (will save with diary)</div>
                      <ul className="list-group">
                        {pendingAttachments.map((a, idx) => (
                          <li
                            key={`${a.AttachmentPath}_${idx}`}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <span>{a.FileName}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removePendingAttachment(idx)}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {editingDiaryID > 0 ? (
                    <div className="col-md-12">
                      <div className="small fw-semibold mb-1">Saved Attachments</div>
                      {loadingAttachments ? (
                        <div className="small text-muted">Loading attachments...</div>
                      ) : diaryAttachments.length === 0 ? (
                        <div className="small text-muted">No attachments added yet.</div>
                      ) : (
                        <ul className="list-group">
                          {diaryAttachments.map((a) => (
                            <li
                              key={a.ID}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <span>{a.FileName}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteAttachment(a.ID)}
                              >
                                Delete
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}

                  <div className="col-md-12 d-flex gap-3 flex-wrap">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="subReq" checked={IsSubmissionRequired} onChange={(e) => setIsSubmissionRequired(e.target.checked)} />
                      <label className="form-check-label" htmlFor="subReq">Submission required</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="allowCom" checked={AllowComments} onChange={(e) => setAllowComments(e.target.checked)} />
                      <label className="form-check-label" htmlFor="allowCom">Allow comments</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="notifyApp" checked={NotifyByApp} onChange={(e) => setNotifyByApp(e.target.checked)} />
                      <label className="form-check-label" htmlFor="notifyApp">Send push notification (Firebase)</label>
                    </div>
                  </div>

                  <div className="col-md-12 text-end">
                    {editingDiaryID > 0 ? (
                      <button
                        type="button"
                        className="button button-white me-2"
                        onClick={cancelEdit}
                      >
                        Cancel Edit
                      </button>
                    ) : null}
                    <button type="button" className="button button-white me-2" disabled={saving} onClick={() => handleSave(false)}>
                      {editingDiaryID > 0 ? "Update Draft" : "Save as Draft"}
                    </button>
                    <button type="button" className="button button-primary" disabled={saving} onClick={() => handleSave(true)}>
                      {saving ? "Saving..." : editingDiaryID > 0 ? "Update & Publish" : "Publish"}
                    </button>
                  </div>
                </div>
              </Form>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">My Diaries</h6>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn ${statusFilter === "Draft" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setStatusFilter("Draft")}
                  >
                    Drafts
                  </button>
                  <button
                    type="button"
                    className={`btn ${statusFilter === "Published" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setStatusFilter("Published")}
                  >
                    Published
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-theme table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Class/Section</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingList ? (
                      <tr><td colSpan="7" className="text-center"><Spinner animation="border" /></td></tr>
                    ) : myDiaries.length === 0 ? (
                      <tr><td colSpan="7" className="text-center">No diaries found.</td></tr>
                    ) : (
                      myDiaries.map((d) => (
                        <tr key={d.ID}>
                          <td>{d.ID}</td>
                          <td>{String(d.Dated || "").substring(0, 10)}</td>
                          <td>{d.DiaryTitle}</td>
                          <td>{d.ClassID}{d.SectionID ? ` / ${d.SectionID}` : ""}</td>
                          <td>{d.DiaryTypeID}</td>
                          <td>{d.IsPublished ? "Published" : "Draft"}</td>
                          <td className="text-end">
                            {!d.IsPublished ? (
                              <>
                                <button className="button button-white button-sm me-2" onClick={() => startEditDraft(d)}>
                                  Edit
                                </button>
                                <button className="button button-primary button-sm" onClick={() => publishDraft(d.ID)}>
                                  Publish
                                </button>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-6">
                  <label className="mb-1">Review Diary Status (select diary)</label>
                  <select
                    className="form-select"
                    value={selectedDiaryForReview}
                    onChange={(e) => {
                      const diaryId = parseInt(e.target.value, 10) || 0;
                      setSelectedDiaryForReview(diaryId);
                      loadReviewPanels(diaryId);
                    }}
                  >
                    <option value="0">Please Select</option>
                    {reviewDiaries.map((d) => (
                      <option key={d.ID} value={d.ID}>
                        #{d.ID} - {d.DiaryTitle}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {reviewLoading ? (
                <div className="mt-3 text-center"><Spinner animation="border" /></div>
              ) : selectedDiaryForReview > 0 ? (
                <div className="mt-3">
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Student Read Status</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-theme">
                          <thead>
                            <tr>
                              <th>Reg #</th>
                              <th>Student</th>
                              <th>Read</th>
                              <th>Read On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {readStatus.length === 0 ? (
                              <tr><td colSpan="4" className="text-center">No records.</td></tr>
                            ) : (
                              readStatus.map((r) => (
                                <tr key={r.ID}>
                                  <td>{r.Registration_No}</td>
                                  <td>{r.Student_Name}</td>
                                  <td>{r.IsRead ? "Yes" : "No"}</td>
                                  <td>{r.ReadOn ? String(r.ReadOn).replace("T", " ").substring(0, 19) : "-"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6>Parent Acknowledgement</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-theme">
                          <thead>
                            <tr>
                              <th>Reg #</th>
                              <th>Student</th>
                              <th>Acknowledged</th>
                              <th>Acknowledged On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ackStatus.length === 0 ? (
                              <tr><td colSpan="4" className="text-center">No records.</td></tr>
                            ) : (
                              ackStatus.map((a) => (
                                <tr key={a.ID}>
                                  <td>{a.Registration_No}</td>
                                  <td>{a.Student_Name}</td>
                                  <td>{a.ParentAcknowledged ? "Yes" : "No"}</td>
                                  <td>{a.AcknowledgedOn ? String(a.AcknowledgedOn).replace("T", " ").substring(0, 19) : "-"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <h6 className="mt-3">Submission Review</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-theme">
                      <thead>
                        <tr>
                          <th>Reg #</th>
                          <th>Student</th>
                          <th>Submitted On</th>
                          <th>Status</th>
                          <th>Submission</th>
                          <th>Teacher Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissionReview.length === 0 ? (
                          <tr><td colSpan="6" className="text-center">No submissions yet.</td></tr>
                        ) : (
                          submissionReview.map((s) => (
                            <tr key={s.ID}>
                              <td>{s.Registration_No}</td>
                              <td>{s.Student_Name}</td>
                              <td>{s.SubmittedOn ? String(s.SubmittedOn).replace("T", " ").substring(0, 19) : "-"}</td>
                              <td>{s.Status || "-"}</td>
                              <td>{s.SubmissionText || "-"}</td>
                              <td>{s.TeacherRemarks || "-"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TeacherDiaryManager;
